/**
 * PDF Review Matcher
 *
 * Pure functions for matching extracted field values against the pdf.js text layer.
 * Finds where extracted data appears on the rendered PDF pages.
 */

// Values of exactly 1 character are skipped unless clearly meaningful
const MIN_VALUE_LENGTH = 2

// Common boilerplate patterns that produce false-positive matches
const BOILERPLATE_PATTERNS = [
  /^page\s+\d+(\s+of\s+\d+)?$/i,
  /^(continued|subtotal|total|amount|qty|quantity|description|item|unit|price|date|no\.?)$/i
]

// Field path prefixes that are system/metadata and will never appear on a PDF.
// These are pre-filtered before matching to avoid wasting cycles.
const NON_PDF_PATH_PREFIXES = [
  'source_locator',
  'input_modality',
  'transaction_type',
  'processing_metadata',
  'extraction_metadata',
  'unique_identifier',
  'summarized_content'
]

/**
 * Returns true if the field path is metadata that won't appear on the PDF.
 */
export function isNonPdfField(fieldPath) {
  if (!fieldPath) return false
  const root = fieldPath.split('.')[0]
  return NON_PDF_PATH_PREFIXES.includes(root)
}

/**
 * Normalize text for fuzzy comparison: collapse whitespace, lowercase, strip
 * currency symbols and common punctuation that doesn't affect identity.
 */
export function normalizeText(text) {
  if (!text) return ''
  return text
    .toString()
    .normalize('NFKC')
    .toLowerCase()
    .replace(/[\u200B-\u200D\uFEFF]/g, '')
    .replace(/[$€£¥]/g, '')
    .replace(/[,]/g, '')
    .replace(/[‐‑‒–—−]/g, '-')
    .replace(/\s+/g, ' ')
    .trim()
}

/**
 * Normalize a numeric value for comparison. Handles currency formatting,
 * thousand separators, and trailing zeros.
 */
export function normalizeNumeric(value) {
  const str = value.toString().replace(/[$€£¥,\s]/g, '')
  const num = parseFloat(str)
  if (isNaN(num)) return null
  return num
}

const MONTHS = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec']
const MONTH_NAME = String.raw`(?:jan(?:uary)?|feb(?:ruary)?|mar(?:ch)?|apr(?:il)?|may|june?|july?|aug(?:ust)?|sept?(?:ember)?|oct(?:ober)?|nov(?:ember)?|dec(?:ember)?)\.?`
const ORDINAL = '(?:st|nd|rd|th)?'
// A clock time after the date ("2026-06-25T14:30:00Z", "6/25/2026 2:30 PM").
const TIME_SUFFIX = String.raw`(?:[ T,]+\d{1,2}:\d{2}(?::\d{2}(?:\.\d+)?)?\s*(?:[ap]\.?m\.?)?\s*(?:z|utc|[+-]\d{2}:?\d{2})?)$`
const WITH_TIME = new RegExp(TIME_SUFFIX, 'i')
const ISO_DATE = /^(\d{4})-(\d{1,2})-(\d{1,2})$/
const NUMERIC_DATE = /^(\d{1,2})([/.-])(\d{1,2})\2(\d{4}|\d{2})$/
const MONTH_FIRST_DATE = new RegExp(String.raw`^(${MONTH_NAME})[\s-]*(\d{1,2})${ORDINAL},?[\s-]+(\d{4})$`, 'i')
const DAY_FIRST_DATE = new RegExp(String.raw`^(\d{1,2})${ORDINAL}[\s-]+(${MONTH_NAME})[\s,-]+(\d{4})$`, 'i')
// A date written inside a longer run ("Statement Date: 6/1/2026"), standing
// on its own: never part of a size or a part number ("3/8-16", "1/2-13").
const DATE_IN_TEXT = new RegExp([
  String.raw`(?<![\d/.-])(?:\d{4}-\d{1,2}-\d{1,2}|\d{1,2}([/.-])\d{1,2}\1(?:\d{4}|\d{2}))(?![\d/-]|\.\d)`,
  String.raw`\b${MONTH_NAME}[\s-]*\d{1,2}${ORDINAL},?[\s-]+\d{4}\b`,
  String.raw`\b\d{1,2}${ORDINAL}[\s-]+${MONTH_NAME}[\s,-]+\d{4}\b`
].join('|'), 'gi')

function isoDate(year, month, day) {
  const y = Number(year.length === 2 ? `20${year}` : year)
  const m = Number(month)
  const d = Number(day)
  if (m < 1 || m > 12 || d < 1 || d > new Date(Date.UTC(y, m, 0)).getUTCDate()) return null
  return `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`
}

function monthNumber(name) {
  return String(MONTHS.indexOf(name.toLowerCase().slice(0, 3)) + 1)
}

// A value that is or holds a date ("10/14/2026 (net 30)") is never read as
// a number: its first digits would outline every "10" on the page.
function containsDate(value) {
  return Boolean(normalizeDate(value)) || (value.toString().match(DATE_IN_TEXT) || []).some(normalizeDate)
}

/**
 * Normalize a date value to YYYY-MM-DD, or null when the value is not a date.
 * The whole value must be a date (a clock time after it is fine): ISO
 * (2026-06-25), numeric with one separator (6/25/2026; day first when dotted,
 * 25.06.2026, or when the first number cannot be a month, 25/06/2026), or with
 * the month's name ("June 25, 2026", "25 Jun 2026"). "3/8-16 x 1-1/2" is not.
 */
export function normalizeDate(value) {
  const str = value.toString().trim().replace(WITH_TIME, '').trim()
  let match = str.match(ISO_DATE)
  if (match) return isoDate(match[1], match[2], match[3])

  match = str.match(NUMERIC_DATE)
  if (match) {
    const [, first, separator, second, year] = match
    const dayFirst = separator === '.' || Number(first) > 12
    return dayFirst ? isoDate(year, second, first) : isoDate(year, first, second)
  }

  match = str.match(MONTH_FIRST_DATE)
  if (match) return isoDate(match[3], monthNumber(match[1]), match[2])

  match = str.match(DAY_FIRST_DATE)
  if (match) return isoDate(match[3], monthNumber(match[2]), match[1])

  return null
}

/**
 * Determine if a value is too short/ambiguous to match reliably.
 * Short numeric values (like quantities) are fine if they're part of a
 * structured line item — context filtering handles disambiguation.
 */
export function isValueMatchable(value, fieldPath) {
  if (!value) return false
  const str = value.toString().trim()
  if (str.length === 0) return false

  // Dates are always matchable (they're long enough after normalization)
  if (normalizeDate(str)) return true

  const isArrayItem = /\.\d+\./.test(fieldPath || '')
  const normalized = normalizeText(str)

  if (normalized.length < MIN_VALUE_LENGTH) {
    // Single-char values: only match single digits in array-item context
    if (isArrayItem && /^\d$/.test(normalized)) return true
    return false
  }

  // Skip single digits outside of array-item context (match "1" to page numbers etc.)
  if (/^\d$/.test(normalized) && !isArrayItem) return false

  // Skip values that are just common column headers/labels
  for (const pattern of BOILERPLATE_PATTERNS) {
    if (pattern.test(str)) return false
  }

  return true
}

/**
 * Collect all text spans from the pdf.js text layer for a given page element.
 * Uses page-relative offsets (not viewport rects) to remain valid after scrolling.
 * Returns [{el, text, normalizedText, pageOffset}]
 */
export function collectPageSpans(pageElement) {
  const textLayer = pageElement.querySelector('.textLayer')
  if (!textLayer) return []

  const results = []
  let lineIndex = 0
  let sawLineBreak = false

  // A tagged PDF wraps its runs in `span.markedContent` containers, which have
  // no box of their own: measuring one puts every outline at the page corner.
  // Walk into them and keep only the runs.
  const visit = node => {
    if (node.nodeName === 'BR') {
      sawLineBreak = true
      lineIndex += 1
      return
    }

    if (!(node instanceof HTMLElement) || node.tagName !== 'SPAN') return
    if (node.classList.contains('markedContent')) {
      node.childNodes.forEach(visit)
      return
    }

    const text = node.textContent
    if (!text || !text.trim()) return

    results.push({
      el: node,
      text: text,
      normalizedText: normalizeText(text),
      lineIndex
    })
  }
  textLayer.childNodes.forEach(visit)

  if (results.length === 0) {
    textLayer.querySelectorAll('span').forEach(span => {
      const text = span.textContent
      if (!text || !text.trim()) return

      results.push({
        el: span,
        text: text,
        normalizedText: normalizeText(text),
        lineIndex: visualLineKey({ el: span })
      })
    })
  } else if (!sawLineBreak) {
    results.forEach(span => {
      span.lineIndex = visualLineKey(span)
    })
  }

  return results
}

/**
 * Collect text content from all pages. Returns a map: pageIndex → spans[].
 */
export function collectAllSpans(viewerContainer) {
  const pages = viewerContainer.querySelectorAll('.page')
  const pageMap = new Map()

  pages.forEach((page, index) => {
    pageMap.set(index, collectPageSpans(page))
  })

  return pageMap
}

/**
 * Compute a span's position relative to its page element (scroll-stable).
 */
export function getSpanPageOffset(span, pageElement) {
  const spanRect = span.getBoundingClientRect()
  const pageRect = pageElement.getBoundingClientRect()
  return {
    left: spanRect.left - pageRect.left,
    top: spanRect.top - pageRect.top,
    width: spanRect.width,
    height: spanRect.height
  }
}

/**
 * Compute the vertical center of matched spans relative to their page.
 * Used for context-aware proximity scoring.
 */
export function getMatchVerticalCenter(match, viewerContainer) {
  const pages = viewerContainer.querySelectorAll('.page')
  const pageEl = pages[match.pageIndex]
  if (!pageEl || !match.spans.length) return { x: 0, y: 0 }

  const pageRect = pageEl.getBoundingClientRect()
  let top = Infinity, bottom = -Infinity, left = Infinity, right = -Infinity

  match.spans.forEach(span => {
    const r = span.el.getBoundingClientRect()
    top = Math.min(top, r.top - pageRect.top)
    bottom = Math.max(bottom, r.bottom - pageRect.top)
    left = Math.min(left, r.left - pageRect.left)
    right = Math.max(right, r.right - pageRect.left)
  })

  return { x: (left + right) / 2, y: (top + bottom) / 2 }
}

/**
 * Match a single value against page spans. Returns match results.
 *
 * @param {string} value - The extracted field value to find
 * @param {Map} pageSpans - Map of pageIndex → spans[]
 * @param {string} fieldPath - Dot-notation path for context filtering
 * @returns {{status: 'matched'|'multiple'|'unmatched'|'skipped', matches: Array, confidence: number}}
 */
export function findValueMatches(value, pageSpans, fieldPath) {
  if (!value || value.toString().trim() === '') {
    return { status: 'unmatched', matches: [], confidence: 0 }
  }

  // Skip system/metadata fields that won't appear on a PDF
  if (isNonPdfField(fieldPath)) {
    return { status: 'skipped', matches: [], confidence: 0 }
  }

  if (!isValueMatchable(value, fieldPath)) {
    return { status: 'skipped', matches: [], confidence: 0 }
  }

  const normalizedValue = normalizeText(value)
  if (!normalizedValue) return { status: 'unmatched', matches: [], confidence: 0 }

  const matches = []
  let confidence = 0

  // Strategy 1: Exact normalized substring match (highest confidence)
  for (const [pageIndex, spans] of pageSpans) {
    const exactMatches = findExactSubstringMatches(normalizedValue, spans, pageIndex)
    matches.push(...exactMatches)
  }

  if (matches.length > 0) {
    confidence = matches.length === 1 ? 1.0 : 0.7
    return {
      status: matches.length === 1 ? 'matched' : 'multiple',
      matches,
      confidence
    }
  }

  // A date matches as that date however it is written. Not found that way,
  // the text strategies below may still find it, but never the numeric one:
  // read as a number, "11/29/2031" is 11.
  const normalizedDate = normalizeDate(value)
  if (normalizedDate) {
    for (const [pageIndex, spans] of pageSpans) {
      matches.push(...findDateMatches(normalizedDate, spans, pageIndex))
    }
    if (matches.length > 0) {
      return { status: matches.length === 1 ? 'matched' : 'multiple', matches, confidence: matches.length === 1 ? 0.95 : 0.6 }
    }
  }

  // Strategy 2: Numeric match
  const numericValue = containsDate(value) ? null : normalizeNumeric(value)
  if (numericValue !== null) {
    for (const [pageIndex, spans] of pageSpans) {
      const numMatches = findNumericMatches(numericValue, spans, pageIndex)
      matches.push(...numMatches)
    }
    if (matches.length > 0) {
      confidence = matches.length === 1 ? 0.9 : 0.5
      return {
        status: matches.length === 1 ? 'matched' : 'multiple',
        matches,
        confidence
      }
    }
  }

  // Strategy 4: Token window match (for multi-word values)
  const tokens = normalizedValue.split(' ').filter(t => t.length > 1)
  if (tokens.length >= 2) {
    for (const [pageIndex, spans] of pageSpans) {
      const tokenMatches = findTokenWindowMatches(tokens, spans, pageIndex)
      matches.push(...tokenMatches)
    }
    if (matches.length > 0) {
      confidence = matches.length === 1 ? 0.75 : 0.4
      return {
        status: matches.length === 1 ? 'matched' : 'multiple',
        matches,
        confidence
      }
    }
  }

  // Strategy 5: Same-line contains. PDF.js often separates address blocks
  // with <br> nodes, so treating visual lines as match windows catches
  // values that are fragmented across adjacent spans.
  if (normalizedValue.length >= 4) {
    for (const [pageIndex, spans] of pageSpans) {
      const lineMatches = findLineContainsMatches(normalizedValue, spans, pageIndex)
      matches.push(...lineMatches)
    }
    if (matches.length > 0) {
      confidence = matches.length === 1 ? 0.65 : 0.35
      return {
        status: matches.length === 1 ? 'matched' : 'multiple',
        matches,
        confidence
      }
    }
  }

  // Strategy 6: Relaxed contains (no token boundary requirement)
  // Catches text embedded inside longer spans without clean word breaks
  if (normalizedValue.length >= 4) {
    for (const [pageIndex, spans] of pageSpans) {
      const relaxedMatches = findRelaxedContainsMatches(normalizedValue, spans, pageIndex)
      matches.push(...relaxedMatches)
    }
    if (matches.length > 0) {
      confidence = matches.length === 1 ? 0.6 : 0.3
      return {
        status: matches.length === 1 ? 'matched' : 'multiple',
        matches,
        confidence
      }
    }
  }

  return { status: 'unmatched', matches: [], confidence: 0 }
}

/**
 * Find exact normalized substring matches across concatenated span text.
 * First checks individual spans (fast path), then tries cross-span concatenation.
 */
function findExactSubstringMatches(normalizedValue, spans, pageIndex) {
  const fullSpanMatches = []
  const substringMatches = []

  // Fast path: prefer values that occupy the full PDF text span. This prevents
  // a clean header address line from losing to the same text embedded in footer
  // boilerplate or legal copy.
  spans.forEach(span => {
    const idx = span.normalizedText.indexOf(normalizedValue)
    if (idx === -1) return
    if (!hasTokenBoundaries(span.normalizedText, idx, normalizedValue.length)) return

    const match = {
      pageIndex,
      spans: [span],
      type: span.normalizedText === normalizedValue ? 'exact_full_span' : 'exact'
    }

    if (match.type === 'exact_full_span') {
      fullSpanMatches.push(match)
    } else {
      substringMatches.push(match)
    }
  })

  if (fullSpanMatches.length > 0) return fullSpanMatches
  if (substringMatches.length > 0) return substringMatches

  // Cross-span matching: build concatenated text with span boundary markers
  let concat = ''
  const spanOffsets = [] // [{start, end, spanIndex}]

  spans.forEach((span, i) => {
    const start = concat.length
    concat += span.normalizedText
    spanOffsets.push({ start, end: concat.length, spanIndex: i })
    // Add space between spans to avoid false joins
    concat += ' '
    spanOffsets.push({ start: concat.length - 1, end: concat.length, spanIndex: -1 })
  })

  // Search for the value in the concatenated text
  let searchStart = 0
  while (true) {
    const idx = concat.indexOf(normalizedValue, searchStart)
    if (idx === -1) break

    if (!hasTokenBoundaries(concat, idx, normalizedValue.length)) {
      searchStart = idx + 1
      continue
    }

    // Find which spans this match covers
    const matchEnd = idx + normalizedValue.length
    const coveredSpans = spanOffsets
      .filter(s => s.spanIndex >= 0 && s.start < matchEnd && s.end > idx)
      .map(s => spans[s.spanIndex])

    if (coveredSpans.length > 0) {
      const matchText = normalizeText(coveredSpans.map(span => span.text).join(' '))
      const isFullCrossSpanMatch = matchText === normalizedValue

      fullSpanMatches.push({
        pageIndex,
        spans: coveredSpans,
        type: isFullCrossSpanMatch ? 'exact_full_line' : 'exact'
      })
    }

    searchStart = idx + 1
  }

  const fullCrossSpanMatches = fullSpanMatches.filter(match => match.type === 'exact_full_line')
  return fullCrossSpanMatches.length > 0 ? fullCrossSpanMatches : fullSpanMatches
}

/**
 * Find numeric value matches (handles different formatting of same number).
 */
function findNumericMatches(numericValue, spans, pageIndex) {
  const matches = []

  spans.forEach(span => {
    // Extract numbers from the span text
    const numberPattern = /[\d,]+(?:\.\d+)?/g
    let match
    while ((match = numberPattern.exec(span.text)) !== null) {
      if (!hasTokenBoundaries(span.text, match.index, match[0].length)) continue

      const spanNumeric = normalizeNumeric(match[0])
      if (spanNumeric !== null && Math.abs(spanNumeric - numericValue) < 0.001) {
        matches.push({
          pageIndex,
          spans: [span],
          type: 'numeric'
        })
      }
    }
  })

  return matches
}

function hasTokenBoundaries(text, start, length) {
  const before = start > 0 ? text[start - 1] : ''
  const after = start + length < text.length ? text[start + length] : ''

  return !isAlphaNumeric(before) && !isAlphaNumeric(after)
}

function isAlphaNumeric(char) {
  return /[a-z0-9]/i.test(char)
}

/**
 * Find date matches by normalizing dates on both sides.
 */
function findDateMatches(normalizedDate, spans, pageIndex) {
  const matches = []

  spans.forEach((span, i) => {
    // Try the span itself, or a date written inside it ("Date: 6/25/2026")
    const spanDate = normalizeDate(span.text.trim())
    const inner = (span.text.match(DATE_IN_TEXT) || []).map(normalizeDate)
    if (spanDate === normalizedDate || inner.includes(normalizedDate)) {
      matches.push({
        pageIndex,
        spans: [span],
        type: 'date'
      })
      return
    }

    // Try span + next span (dates sometimes split across elements)
    if (i < spans.length - 1) {
      const next = spans[i + 1].text
      const combinedDate = normalizeDate(`${span.text}${next}`) || normalizeDate(`${span.text} ${next}`)
      if (combinedDate === normalizedDate) {
        matches.push({
          pageIndex,
          spans: [span, spans[i + 1]],
          type: 'date'
        })
      }
    }
  })

  return matches
}

/**
 * Token window match: slide an N-token window across span text,
 * match if ≥80% of value tokens are found in the window.
 */
function findTokenWindowMatches(valueTokens, spans, pageIndex) {
  const matches = []
  const threshold = 0.8
  const requiredMatches = Math.ceil(valueTokens.length * threshold)

  // Build token list from spans with span references
  const spanTokens = []
  spans.forEach(span => {
    const tokens = span.normalizedText.split(' ').filter(t => t.length > 0)
    tokens.forEach(token => {
      spanTokens.push({ token, span })
    })
  })

  // Slide window of valueTokens.length across spanTokens
  const windowSize = Math.min(valueTokens.length + 2, spanTokens.length)
  for (let i = 0; i <= spanTokens.length - windowSize; i++) {
    const window = spanTokens.slice(i, i + windowSize)
    const windowTokenSet = new Set(window.map(st => st.token))

    let matchCount = 0
    valueTokens.forEach(vt => {
      if (windowTokenSet.has(vt)) matchCount++
    })

    if (matchCount >= requiredMatches) {
      const uniqueSpans = [...new Set(window.map(st => st.span))]
      matches.push({
        pageIndex,
        spans: uniqueSpans,
        type: 'token_window'
      })
      // Skip ahead to avoid duplicate matches for same region
      i += windowSize - 1
    }
  }

  return matches
}

/**
 * Match against visual/text-layer lines. This catches values that are present
 * in address/header blocks but split across spans around PDF.js <br> nodes.
 */
function findLineContainsMatches(normalizedValue, spans, pageIndex) {
  const matches = []
  const lineGroups = new Map()

  spans.forEach(span => {
    const key = span.lineIndex ?? visualLineKey(span)
    if (!lineGroups.has(key)) lineGroups.set(key, [])
    lineGroups.get(key).push(span)
  })

  lineGroups.forEach(lineSpans => {
    const lineText = normalizeText(lineSpans.map(span => span.text).join(' '))
    if (!lineText.includes(normalizedValue)) return

    matches.push({
      pageIndex,
      spans: lineSpans,
      type: 'line_contains'
    })
  })

  return matches
}

function visualLineKey(span) {
  const top = parseFloat(span.el?.style?.top)
  if (!Number.isFinite(top)) return 0

  return Math.round(top * 10) / 10
}

/**
 * Relaxed contains match — finds value as a substring within spans
 * without requiring token boundaries. Lower confidence but catches
 * addresses and values embedded in longer PDF text spans.
 */
function findRelaxedContainsMatches(normalizedValue, spans, pageIndex) {
  const matches = []

  // Check individual spans
  spans.forEach(span => {
    if (span.normalizedText.includes(normalizedValue)) {
      matches.push({
        pageIndex,
        spans: [span],
        type: 'relaxed'
      })
    }
  })

  if (matches.length > 0) return matches

  // Cross-span concatenation (no boundary check)
  let concat = ''
  const spanOffsets = []

  spans.forEach((span, i) => {
    const start = concat.length
    concat += span.normalizedText
    spanOffsets.push({ start, end: concat.length, spanIndex: i })
    concat += ' '
    spanOffsets.push({ start: concat.length - 1, end: concat.length, spanIndex: -1 })
  })

  let searchStart = 0
  while (true) {
    const idx = concat.indexOf(normalizedValue, searchStart)
    if (idx === -1) break

    const matchEnd = idx + normalizedValue.length
    const coveredSpans = spanOffsets
      .filter(s => s.spanIndex >= 0 && s.start < matchEnd && s.end > idx)
      .map(s => spans[s.spanIndex])

    if (coveredSpans.length > 0) {
      matches.push({
        pageIndex,
        spans: coveredSpans,
        type: 'relaxed'
      })
    }

    searchStart = idx + 1
  }

  return matches
}

/**
 * @returns {Map} fieldPath → {status, matches, confidence}
 */
export function matchAllFields(fields, pageSpans, viewerContainer) {
  const results = new Map()

  fields.forEach(field => {
    const result = findValueMatches(field.value, pageSpans, field.path)
    results.set(field.path, result)
  })

  return prioritizeByContext(fields, results, viewerContainer)
}

/**
 * Async chunked matching for large field sets. Processes fields in batches
 * across requestAnimationFrame boundaries to keep the UI responsive.
 *
 * @param {Array} fields - All fields to match
 * @param {Map} pageSpans - Page spans from collectAllSpans
 * @param {Element} viewerContainer - The PDF viewer element
 * @param {Object} options
 * @param {number} options.chunkSize - Fields per chunk (default: 40)
 * @param {Function} options.onProgress - Called with partial results after each chunk
 * @returns {Promise<Map>} Final match results
 */
export function matchFieldsChunked(fields, pageSpans, viewerContainer, options = {}) {
  const { chunkSize = 40, onProgress } = options
  const results = new Map()

  return new Promise((resolve) => {
    let offset = 0

    function processChunk() {
      const end = Math.min(offset + chunkSize, fields.length)

      for (let i = offset; i < end; i++) {
        const field = fields[i]
        const result = findValueMatches(field.value, pageSpans, field.path)
        results.set(field.path, result)
      }

      offset = end

      if (offset < fields.length) {
        if (onProgress) onProgress(results)
        requestAnimationFrame(processChunk)
      } else {
        // All fields processed — run context prioritization
        const final = prioritizeByContext(fields, results, viewerContainer)
        resolve(final)
      }
    }

    requestAnimationFrame(processChunk)
  })
}

/**
 * Use sibling field proximity to disambiguate multiple matches.
 * Works for both array items (line_items.0.*) and object siblings (address.*).
 */
function prioritizeByContext(fields, results, viewerContainer) {
  const groups = new Map()

  fields.forEach(field => {
    const groupKey = fieldGroupKey(field.path)
    if (!groupKey) return

    if (!groups.has(groupKey)) groups.set(groupKey, [])
    groups.get(groupKey).push(field)
  })

  groups.forEach(groupFields => {
    const anchors = []

    groupFields.forEach(field => {
      if (isWeakContextAnchor(field.value)) return

      const result = results.get(field.path)
      if (!result || result.matches.length === 0) return

      result.matches.forEach(match => {
        anchors.push({
          fieldPath: field.path,
          pageIndex: match.pageIndex,
          center: matchCenterLive(match, viewerContainer)
        })
      })
    })

    if (anchors.length === 0) return

    groupFields.forEach(field => {
      const result = results.get(field.path)
      if (!result || result.matches.length <= 1) return

      const siblingAnchors = anchors.filter(anchor => anchor.fieldPath !== field.path)
      if (siblingAnchors.length === 0) return

      const scoredMatches = result.matches.map(match => ({
        match,
        score: contextScore(match, siblingAnchors, viewerContainer)
      }))
      const contextualMatches = scoredMatches
        .filter(({ score }) => score <= 48)
        .sort((a, b) => a.score - b.score)

      if (contextualMatches.length === 0) return

      const bestScore = contextualMatches[0].score
      const closeMatches = contextualMatches
        .filter(({ score }) => score <= bestScore + 8)
        .map(({ match }) => match)

      results.set(field.path, {
        ...result,
        status: closeMatches.length === 1 ? 'matched' : 'multiple',
        matches: closeMatches,
        confidence: closeMatches.length === 1 ? Math.min(result.confidence + 0.1, 1.0) : result.confidence * 0.8,
        contextFiltered: true
      })
    })
  })

  return results
}

/**
 * Get the group key for a field path. Groups siblings that can provide
 * proximity context for each other:
 * - Array items: "line_items.0.description" → "line_items.0"
 * - Object fields: "payment_address.city" → "payment_address"
 */
function fieldGroupKey(path) {
  const parts = String(path || '').split('.')
  if (parts.length < 2) return null

  // For array items, group by the array + index (e.g., "line_items.0")
  const arrayIndex = parts.findIndex((part, partIndex) => partIndex > 0 && /^\d+$/.test(part))
  if (arrayIndex > 0) {
    return parts.slice(0, arrayIndex + 1).join('.')
  }

  // For object fields with 2+ siblings, group by parent object
  // e.g., "payment_remittance_address.city" → "payment_remittance_address"
  if (parts.length >= 2) {
    return parts.slice(0, -1).join('.')
  }

  return null
}

// Values that are too generic/short to serve as reliable positional anchors
const WEAK_ANCHOR_VALUES = new Set(['', 'n/a', 'na', 'yes', 'no', 'true', 'false', 'none', 'null', '-', '0'])

function isWeakContextAnchor(value) {
  const normalized = normalizeText(value)
  if (!normalized) return true
  if (normalized.length <= 2) return true
  if (WEAK_ANCHOR_VALUES.has(normalized)) return true

  return false
}

/**
 * Compute match center using live DOM rect (accurate regardless of scroll).
 */
function matchCenterLive(match, viewerContainer) {
  const pages = viewerContainer?.querySelectorAll('.page')
  const pageEl = pages?.[match.pageIndex]
  if (!pageEl || !match.spans.length) return { x: 0, y: 0 }

  const pageRect = pageEl.getBoundingClientRect()
  let top = Infinity, bottom = -Infinity, left = Infinity, right = -Infinity

  match.spans.forEach(span => {
    const r = span.el.getBoundingClientRect()
    top = Math.min(top, r.top - pageRect.top)
    bottom = Math.max(bottom, r.bottom - pageRect.top)
    left = Math.min(left, r.left - pageRect.left)
    right = Math.max(right, r.right - pageRect.left)
  })

  return { x: (left + right) / 2, y: (top + bottom) / 2 }
}

function contextScore(match, anchors, viewerContainer) {
  const center = matchCenterLive(match, viewerContainer)

  return Math.min(...anchors.map(anchor => {
    if (anchor.pageIndex !== match.pageIndex) return 10_000

    return Math.abs(anchor.center.y - center.y)
  }))
}
